import React, { useState, createContext, useContext, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { MD5 } from 'crypto-js'

type Props = {
    children: React.ReactNode;
};

type UserInfo = {
    name: string,
    gravatar_url: string,
    default_topic: string,
    top_type: string | null,
};

type ContextContent = {
    userId: string | null,
    token: string | null,
    refreshToken: string | null,
    userInfo: UserInfo | null,
    refreshAccess: (token: string) => Promise<void>,
    onLogin: (username: string, password: string, remember: boolean) => Promise<void>,
    onLogout: (token: string) => Promise<void>,
    setTopType: (type: string) => void,
};

const PensieveBaseUri = 'http://api.epensieve.com/';

const LOCAL_STORAGE_KEY = 'dev.epensieve.com.user';

const AuthContext = createContext<ContextContent | null>(null);

const AuthProvider = ({ children } : Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);

    useEffect(() => {
        const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedValue) {
            const {user_id, refresh_token} = JSON.parse(storedValue!);
            setUserId(user_id);
            setRefreshToken(refresh_token);
            refreshAccess(refresh_token)
            .then((access_token) => {
                loadUserInfo(user_id, access_token).then()
                .catch((error) => {
                    // Set Defaults for User Info
                    setUserInfo({
                        name: '', default_topic: '', top_type: null,
                        gravatar_url: `https://www.gravatar.com/avatar/${MD5(user_id).toString()}?s=128&d=mosterid`
                    })
                })
            })
            .catch(() => {
                // Failed to Refresh, Token probably expired, reset values
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                setUserId(null);
                setRefreshToken(null);
            });
        }
    }, []);

    const loadUserInfo = async (userId: string, accessToken: string) => {
        let url = `${PensieveBaseUri}users/${userId}`;
        let response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        let {name, gravatar} = await response.json();
        const gravatar_hash = MD5(gravatar || 'pensieve@epensieve.com').toString();

        url = `${PensieveBaseUri}settings`;
        response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        let settings = await response.json();

        setUserInfo({
            name: name || 'Thou Who Shall Not Be Named',
            gravatar_url: `https://www.gravatar.com/avatar/${gravatar_hash}?s=128`,
            ...settings
        });
    }
  
    const handleLogin = async (username: string, password: string, remember: boolean) => {
        const response = await fetch(PensieveBaseUri+'auth/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({username, password})
        });

        if (!response.ok) {
            throw new Error('Could not authenticate this user, please check if the input is correct.');
        }
        
        let result = await response.json();

        setUserId(result.user_id);
        setToken(result.access_token);
        setRefreshToken(result.refresh_token);

        await loadUserInfo(result.user_id, result.access_token);
        try {
            if (remember) {
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({user_id: result.user_id, refresh_token: result.refresh_token}));
            } else {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        } catch (err) {
            console.log('[DEV RUNTIME ERROR] Could not set user locally, so refreshing may loose connection');
        }

        const { from } = location?.state as {from: {pathname: string}} || {from: null};
        const origin = from?.pathname || '/';
        navigate(origin);
    };

    const refreshAccess = async (refresh_token: string) => {
        const response = await fetch(PensieveBaseUri+'auth/refresh', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({refresh_token})
        });

        if (!response.ok) {
            throw new Error('Could not refresh access to this user.');
        }
        
        let result = await response.json();

        setToken(result.access_token);
        return result.access_token;
    };

    const handleLogout = async (refresh_token: string) => {
        await fetch(PensieveBaseUri+'auth/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({refresh_token})
        });

        try {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
        } catch (err) {
            console.log('[DEV RUNTIME ERROR] Error when attempting to clear local key');
        }

        setUserId(null);
        setUserInfo(null);
        setToken(null);
        setRefreshToken(null);
    };

    const setTopType = (type: string) => {
        if(userInfo) {
            setUserInfo({
                ...userInfo!,
                top_type: type
            });
        }
    }

    const value = {
        userId,
        userInfo,
        token,
        refreshToken,
        refreshAccess: refreshAccess,
        onLogin: handleLogin,
        onLogout: handleLogout,
        setTopType: setTopType,
    };
  
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const ProtectedRoute = ({children} : any) => {
    const { token } = useAuth()!;
    const location = useLocation();

    if( !token ) {
        return <Navigate to="/login" replace state={{from: location}} />;
    }
    return children;
};

export const useAuth = () => {
    return useContext(AuthContext);
};

export default AuthProvider;